import {
  CURRENCY_BSV,
  type TBalancesQueryOptions,
} from '@tokenized/sdk-js-private';
import { useIsFetching, useQuery } from 'react-query';
import { usePersonalProfile } from '../entities/usePersonalProfile';
import { useTokenizedApi } from '../useTokenizedApi';
import { useInstrumentListWithDetails } from './useInstrumentListWithDetails';

/**
 * **`React Query hook`** Filtered and sorted list of the quantities (balances)
 * of instruments and liabilities in a specified vault. While the hook is mounted,
 * balances will be refreshed automatically **every 60 seconds**.
 *
 * See https://react-query.tanstack.com/guides/queries for general information
 * about using a React Query `UseQueryResults` object. Once loaded, the `data`
 * property will contain an array of objects, one for each instrument balance. Each
 * object describes a selection of relevant quantities for the particular instrument:
 *
 * - `balance`: Quantity of the instrument held in this vault
 * - `available`: Quantity of balance available to use in transfers
 * - `reserved`: Quantity of balance reserved for pending transactions
 * - `authorizedQuantity`: Total quantity of instrument in existence
 * - `issuedLiability`: For the entity’s own instruments: the quantity issued to others
 *
 * @category Instruments
 */
export function useFilteredBalances(options?: TBalancesQueryOptions) {
  const tokenizedApi = useTokenizedApi();

  const personalProfile = usePersonalProfile();
  const profileId = options?.profileId || personalProfile?.id;
  const vaultId = options?.vaultId;

  const entityId = useQuery(
    tokenizedApi.entities.profilesQuery(),
  ).data?.idMap.get(profileId as string)?.entityId;

  const vaults = useQuery(tokenizedApi.treasury.vaultsQuery(profileId)).data;

  const primaryLockboxId = vaultId
    ? vaults?.idMap.get(vaultId)?.primaryLockboxId
    : undefined;

  const lockboxBalancesResult = useQuery(
    tokenizedApi.treasury.balancesQuery(profileId),
  );
  const lockboxBalances = lockboxBalancesResult.data;
  let balances = lockboxBalances
    ? lockboxBalances.find(({ lockboxId }) => lockboxId == primaryLockboxId)
        ?.balances || []
    : null;

  const instrumentIds = new Set(
    balances?.map(({ instrumentId }) => instrumentId).filter(Boolean),
  );
  if (balances) {
    for (const instrumentId of options?.alwaysAddInstrumentIds || []) {
      if (instrumentId && !instrumentIds.has(instrumentId)) {
        balances = [
          ...balances,
          {
            instrumentId,
            totalUnits: 0,
            availableUnits: 0,
          },
        ];
        instrumentIds.add(instrumentId);
      }
    }
  }

  const instruments = useInstrumentListWithDetails(
    [...instrumentIds],
    profileId,
  );
  const instrumentsById = new Map(
    instruments.map((instrument) => [
      instrument.instrumentId as string,
      instrument,
    ]),
  );

  const contracts = useQuery(
    tokenizedApi.contracts.contractsQuery({ profileId }),
  ).data;

  if (!balances) {
    return null;
  }

  return tokenizedApi.treasury.filterBalances(
    balances,
    instrumentsById,
    contracts,
    entityId,
    options,
  );
}

export function useBalancesAreLoading(profileId?: string) {
  const tokenizedApi = useTokenizedApi();

  const personalProfile = usePersonalProfile();
  profileId = profileId || personalProfile?.id;

  const balancesFetching = useIsFetching(
    tokenizedApi.treasury.balancesQuery.makeCacheKey(profileId),
  );
  const contractsFetching = useIsFetching(
    tokenizedApi.contracts.contractsQuery.makeCacheKey({ profileId }),
  );
  return balancesFetching || contractsFetching;
}
